import type { ComponentConfig } from "@measured/puck";

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";

import ActionPlanActionsList from "./ActionPlanActionsList";
import type {
    ActionPlanActionsListColumnConfig,
    ActionPlanActionsListColumnId,
    ActionPlanActionsListConfigProps,
} from "./types";
import { COLUMN_OPTIONS, normalizeColumnsConfig } from "./types";

type ColumnsEditorProps = {
    value?: ActionPlanActionsListColumnConfig[];
    onChange?: (value: ActionPlanActionsListColumnConfig[]) => void;
    readOnly?: boolean;
};

type SortableRowProps = {
    id: ActionPlanActionsListColumnId;
    label: string;
    visible: boolean;
    readOnly: boolean;
    onToggle: (id: ActionPlanActionsListColumnId, next: boolean) => void;
};

const SortableRow = (props: SortableRowProps) => {
    const { id, label, visible, readOnly, onToggle } = props;
    const theme = useTheme();

    const checkedBackground = theme.palette.primary[500];

    const uncheckedBorder = theme.custom?.grey?.[800] ?? theme.custom?.grey?.[900] ?? theme.palette.divider;

    const uncheckedIcon = (
        <Box
            sx={{
                width: 20,
                height: 20,
                borderRadius: "4px",
                border: `2px solid ${uncheckedBorder}`,
                backgroundColor: theme.palette.background.paper,
                boxSizing: "border-box",
            }}
        />
    );

    const checkedIcon = (
        <Box
            sx={{
                width: 20,
                height: 20,
                borderRadius: "4px",
                background: checkedBackground,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
            }}
        >
            <CheckIcon
                sx={{
                    fontSize: 16,
                    color: theme.palette.common.white,
                }}
            />
        </Box>
    );

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.spacing(1),
                padding: theme.spacing(1, 1.5),
                backgroundColor: theme.palette.background.paper,
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.7 : 1,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                <IconButton
                    size="small"
                    disabled={readOnly}
                    sx={{ cursor: readOnly ? "default" : "grab" }}
                    {...attributes}
                    {...listeners}
                >
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
            </Box>

            <Checkbox
                checked={visible}
                disabled={readOnly}
                onChange={(e) => onToggle(id, e.target.checked)}
                inputProps={{ "aria-label": `${label} visibility` }}
                disableRipple
                icon={uncheckedIcon}
                checkedIcon={checkedIcon}
                sx={{
                    padding: 0,
                    borderRadius: theme.spacing(0.5),
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                }}
            />
        </Box>
    );
};

const ColumnsEditor = (props: ColumnsEditorProps) => {
    const theme = useTheme();
    const { value, onChange, readOnly } = props;

    const columns = normalizeColumnsConfig(value);
    const order: ActionPlanActionsListColumnId[] = columns.map((c) => c.id);
    const visibilityById = new Map(columns.map((c) => [c.id, c.visible] as const));
    const labelById = new Map(COLUMN_OPTIONS.map((o) => [o.id, o.label] as const));

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const commit = (
        nextOrder: ActionPlanActionsListColumnId[],
        nextVisibility: Map<ActionPlanActionsListColumnId, boolean>,
    ) => {
        const next: ActionPlanActionsListColumnConfig[] = nextOrder.map((id) => ({
            id,
            visible: nextVisibility.get(id) === true,
        }));

        onChange?.(next);
    };

    const onDragEnd = (event: DragEndEvent) => {
        if (readOnly) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        if (typeof active.id !== "string" || typeof over.id !== "string") return;
        const activeId = active.id as ActionPlanActionsListColumnId;
        const overId = over.id as ActionPlanActionsListColumnId;

        const oldIndex = order.indexOf(activeId);
        const newIndex = order.indexOf(overId);
        if (oldIndex < 0 || newIndex < 0) return;

        commit(arrayMove(order, oldIndex, newIndex), visibilityById);
    };

    const onToggle = (id: ActionPlanActionsListColumnId, next: boolean) => {
        if (readOnly) return;
        const nextVisibility = new Map(visibilityById);
        nextVisibility.set(id, next);
        commit(order, nextVisibility);
    };

    return (
        <Stack spacing={theme.spacing(1.5)}>
            <NativePuckTitleField label="Columns" icon="edit" />

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                    <Stack spacing={theme.spacing(1)}>
                        {order.map((id) => (
                            <SortableRow
                                key={id}
                                id={id}
                                label={labelById.get(id) ?? id}
                                visible={visibilityById.get(id) === true}
                                readOnly={readOnly === true}
                                onToggle={onToggle}
                            />
                        ))}
                    </Stack>
                </SortableContext>
            </DndContext>
        </Stack>
    );
};

export const ActionPlanActionsListBlock: ComponentConfig<ActionPlanActionsListConfigProps> = {
    label: "Action plan actions list",
    fields: {
        columns: {
            type: "custom",
            render: (props: ColumnsEditorProps) => <ColumnsEditor {...props} />,
        },
    },
    defaultProps: {
        columns: normalizeColumnsConfig(undefined),
    },
    render: (props: ActionPlanActionsListConfigProps) => {
        return <ActionPlanActionsList {...props} />;
    },
};

export type { ActionPlanActionsListConfigProps } from "./types";

export default ActionPlanActionsListBlock;
